/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, { useEffect, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import './fileUpload.scss';
import dayjs from 'dayjs';
import { AuthContext } from '../../../Contexts/AuthContext';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import UseAxios from '../../../Api/http/http';
import { MODULE_NAMES } from '../../../Constants/modules';
import {
  fetchFileUploadSummary,
  submitProjectFileUpload,
  updateLastEffortUploadedDate,
  getProjectFileUploadSampleFile,
} from '../../../Api/request/projectFileUpload';
import PuzzleLoader from '../../../Assets/images/svg/loading puzzle.gif';
import {
  FILE_UPLOAD_TYPES,
  normalizeHolidayRows,
  normalizeJiraRows,
  normalizeLeaveRows,
  normalizeLeaveTestRows,
  normalizeProjectExpenseRows,
  normalizeRedmineRows,
  normalizeSalaryRows,
} from './fileUploadUtils';

const UPLOAD_CARDS = [
  {
    key: FILE_UPLOAD_TYPES.REDMINE,
    title: 'Upload Redmine Data',
    gcpType: 'redmine_data',
    uploadLabel: 'Upload',
    module: MODULE_NAMES.PROJECT_FILE_UPLOAD_REDMINE,
  },
  {
    key: FILE_UPLOAD_TYPES.LEAVE,
    title: 'Upload Leave Data',
    gcpType: 'leave_details',
    uploadLabel: 'Upload',
    module: MODULE_NAMES.PROJECT_FILE_UPLOAD_LEAVE,
  },
  {
    key: FILE_UPLOAD_TYPES.HOLIDAY,
    title: 'Upload Holiday Data',
    gcpType: 'holiday_list',
    uploadLabel: 'Upload',
    module: MODULE_NAMES.PROJECT_FILE_UPLOAD_HOLIDAY,
  },
  {
    key: FILE_UPLOAD_TYPES.LEAVE_TEST,
    title: 'Upload Leave Data for Testing',
    gcpType: 'leave_test',
    uploadLabel: 'Upload',
    module: MODULE_NAMES.PROJECT_FILE_UPLOAD_LEAVE_TEST,
  },
  {
    key: FILE_UPLOAD_TYPES.SALARY,
    title: 'Upload Salary Data',
    gcpType: 'salary_upload',
    uploadLabel: 'Upload',
    module: MODULE_NAMES.PROJECT_FILE_UPLOAD_SALARY,
  },
  {
    key: FILE_UPLOAD_TYPES.PROJECT_EXPENSE,
    title: 'Upload Project Expense',
    gcpType: 'project_expense',
    uploadLabel: 'Upload',
    module: MODULE_NAMES.PROJECT_FILE_UPLOAD_PROJECT_EXPENSE,
  },
  {
    key: FILE_UPLOAD_TYPES.JIRA,
    title: 'Upload Jira Effort Data',
    gcpType: 'jira_effort_details',
    uploadLabel: 'Upload',
    module: MODULE_NAMES.PROJECT_FILE_UPLOAD_JIRA,
  },
];

const normalizeDateLabel = (value) => {
  if (!value) return 'Not uploaded yet';
  const date = dayjs(value);
  return date.isValid() ? date.format('DD-MMM-YYYY') : value;
};

const getUploadApiBody = (response) => response?.data?.response?.data || response?.data || {};

const getUploadApiStatus = (response) => response?.data?.response?.status || response?.status || 200;

const getUploadErrorMessage = (response, fallbackMessage = 'Oops! Please try again') => {
  const apiBody = getUploadApiBody(response);
  const message = apiBody?.message || apiBody?.error || fallbackMessage;
  const details = Array.isArray(apiBody?.details) ? apiBody.details.join(' | ') : '';

  return details ? `${message}: ${details}` : message;
};

const INVALID_FILE_FORMAT_MESSAGE = 'invalid File formate';

const UploadTile = ({
  card,
  file,
  uploading,
  canAdd,
  canView,
  onFileChange,
  onUpload,
  onClear,
  onSampleDownload,
  onDownload,
}) => {
  const fileName = file?.name || 'file.csv';
  const canAccessDownload = canView || canAdd;

  return (
    <div className="upload-card">
      <div className="upload-card-header">
        <h3>{card.title}</h3>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {card.gcpType ? (
            <button
              type="button"
              className="sample-file-link"
              onClick={() => onSampleDownload(card.gcpType)}
              disabled={!canAccessDownload}
            >
              Sample File
            </button>
          ) : (
            <a href={card.sampleFile} download>
              Sample File
            </a>
          )}

          {card.key === FILE_UPLOAD_TYPES.LEAVE_TEST ? (
            <button
              type="button"
              className="sample-file-link"
              onClick={onDownload}
              disabled={!canAccessDownload}
            >
              Download
            </button>
          ) : null}
        </div>
      </div>

      <div className="upload-card-body">
        <div className="upload-card-left">
          <div className="upload-card-select-row">
            <label className="file-picker">
              <span>Choose file</span>
              <input type="file" accept=".csv" onChange={onFileChange} disabled={!canAdd} />
            </label>

            <div className="selected-file">
              <span>{fileName}</span>
              {file ? (
                <button type="button" className="clear-file-btn" onClick={onClear}>
                  ×
                </button>
              ) : null}
            </div>
          </div>

          <div className="upload-hint">Upload monthly data</div>
        </div>

        <button
          type="button"
          className="upload-button"
          onClick={onUpload}
          disabled={uploading || !canAdd}
          title={!canAdd ? "Can't Upload" : ''}
        >
          {uploading ? (
            <img src={PuzzleLoader} alt="Uploading..." style={{ width: '28px', height: 'auto' }} />
          ) : card.uploadLabel}
        </button>
      </div>
    </div>
  );
};

UploadTile.propTypes = {
  card: PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    sampleFile: PropTypes.string,
    gcpType: PropTypes.string,
    uploadLabel: PropTypes.string.isRequired,
  }).isRequired,
  file: PropTypes.shape({
    name: PropTypes.string,
  }),
  uploading: PropTypes.bool,
  canAdd: PropTypes.bool,
  canView: PropTypes.bool,
  onFileChange: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onSampleDownload: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
};

UploadTile.defaultProps = {
  file: null,
  uploading: false,
  canAdd: false,
  canView: false,
  onDownload: () => { },
};

const FileUpload = () => {
  const { userProfile } = useContext(AuthContext);
  const { snackbarOpen } = useSnackbar();

  // Helper to extract permissions (incorporating parent permission for fallback/backwards-compatibility)
  const getModulePermissions = (moduleKey) => {
    const parentPermission = userProfile?.permissions?.[MODULE_NAMES.PROJECT_FILE_UPLOAD] || [];
    const modulePermission = userProfile?.permissions?.[moduleKey] || [];
    const combined = [...new Set([...parentPermission, ...modulePermission])];
    return {
      canAdd: combined.includes('add') || combined.includes('create'),
      canView: combined.includes('view') || combined.includes('read'),
    };
  };

  const canAddDate = useMemo(() => {
    const redminePerm = getModulePermissions(MODULE_NAMES.PROJECT_FILE_UPLOAD_REDMINE);
    const jiraPerm = getModulePermissions(MODULE_NAMES.PROJECT_FILE_UPLOAD_JIRA);
    return redminePerm.canAdd || jiraPerm.canAdd;
  }, [userProfile]);

  const [lastEffortDate, setLastEffortDate] = useState('');
  const [manualDate, setManualDate] = useState('');
  const [manualDateError, setManualDateError] = useState('');
  const [pageMessage, setPageMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState({});
  const [cardErrors, setCardErrors] = useState({});
  const [loadingCards, setLoadingCards] = useState({});
  const [processedLeaveTestRows, setProcessedLeaveTestRows] = useState(null);

  const cards = useMemo(() => UPLOAD_CARDS, []);

  // Filter visible cards based on View or Add access
  const visibleCards = useMemo(() => {
    return cards.filter((card) => {
      const { canAdd: cardCanAdd, canView: cardCanView } = getModulePermissions(card.module);
      return cardCanAdd || cardCanView;
    });
  }, [cards, userProfile]);

  // Chunk visible cards into rows of 2 for grid layout
  const cardRows = useMemo(() => {
    const rows = [];
    for (let index = 0; index < visibleCards.length; index += 2) {
      rows.push(visibleCards.slice(index, index + 2));
    }
    return rows;
  }, [visibleCards]);

  const cardErrorMessages = useMemo(
    () => Object.values(cardErrors).filter(Boolean),
    [cardErrors],
  );
  const bannerMessages = useMemo(
    () => [
      ...cardErrorMessages,
      manualDateError,
    ].filter(Boolean),
    [cardErrorMessages, manualDateError],
  );
  const bannerVariant = bannerMessages.length > 0 ? 'error' : (pageMessage ? 'success' : '');
  const bannerText = bannerMessages.length > 0 ? bannerMessages : (pageMessage ? [pageMessage] : []);

  const refreshSummary = async () => {
    try {
      const response = await UseAxios(fetchFileUploadSummary());
      const uploadedDate = response?.data?.summary?.lastEffortUploadedDate
        || response?.data?.lastEffortUploadedDate
        || response?.data?.effortUploadedDate
        || '';
      setLastEffortDate(uploadedDate);
      setManualDate(uploadedDate ? dayjs(uploadedDate).format('YYYY-MM-DD') : '');
    } catch (error) {
      setLastEffortDate('');
      setManualDate('');
      console.error(error);
    }
  };

  useEffect(() => {
    refreshSummary();
  }, []);

  const downloadSampleFile = async (gcpType) => {
    const card = cards.find((itemCard) => itemCard.gcpType === gcpType);
    const { canAdd: cardCanAdd, canView: cardCanView } = getModulePermissions(card?.module);

    if (!cardCanView && !cardCanAdd) {
      snackbarOpen('You do not have permission to download sample files.', 'warning');
      return;
    }

    try {
      const response = await UseAxios(getProjectFileUploadSampleFile(gcpType));

      if (response?.data?.isAxiosError || response?.data instanceof Error) {
        const axiosErr = response.data;
        const message = axiosErr?.response?.data?.message
          || axiosErr?.response?.data?.error
          || axiosErr?.message
          || 'Failed to download sample file.';
        snackbarOpen(message, 'warning');
        return;
      }

      const signedUrl = response?.data?.data?.signedUrl;

      if (!signedUrl) {
        throw new Error('Download URL not found.');
      }

      const link = document.createElement('a');
      link.href = signedUrl;
      link.style.display = 'none';
      document.body.append(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || 'Failed to download sample file.';
      snackbarOpen(message, 'warning');
    }
  };

  const setLoading = (key, value) => {
    setLoadingCards((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const setError = (key, message) => {
    setCardErrors((prev) => ({
      ...prev,
      [key]: message,
    }));
  };

  const clearFile = (key) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [key]: null,
    }));
    if (key === FILE_UPLOAD_TYPES.LEAVE_TEST) {
      clearProcessedLeaveTestData();
    }
    setError(key, '');
  };

  const clearProcessedLeaveTestData = () => {
    setProcessedLeaveTestRows(null);
  };

  const resetSelectedFile = (key) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [key]: null,
    }));
  };

  const handleFileChange = (key) => (event) => {
    const nextFile = event.target.files?.[0] || null;
    const isCsvFile = nextFile ? nextFile.name.toLowerCase().endsWith('.csv') : true;

    if (!isCsvFile) {
      setSelectedFiles((prev) => ({
        ...prev,
        [key]: null,
      }));
      if (key === FILE_UPLOAD_TYPES.LEAVE_TEST) {
        clearProcessedLeaveTestData();
      }
      setError(key, INVALID_FILE_FORMAT_MESSAGE);
      setPageMessage('');
      event.target.value = '';
      return;
    }

    setSelectedFiles((prev) => ({
      ...prev,
      [key]: nextFile,
    }));
    if (key === FILE_UPLOAD_TYPES.LEAVE_TEST) {
      clearProcessedLeaveTestData();
    }
    setError(key, '');
    setPageMessage('');
  };

  const normalizeUploadRows = async (key, file) => {
    switch (key) {
    case FILE_UPLOAD_TYPES.REDMINE:
      return normalizeRedmineRows(file);
    case FILE_UPLOAD_TYPES.LEAVE:
      return normalizeLeaveRows(file);
    case FILE_UPLOAD_TYPES.LEAVE_TEST:
      return normalizeLeaveTestRows(file);
    case FILE_UPLOAD_TYPES.HOLIDAY:
      return normalizeHolidayRows(file);
    case FILE_UPLOAD_TYPES.SALARY:
      return normalizeSalaryRows(file);
    case FILE_UPLOAD_TYPES.PROJECT_EXPENSE:
      return normalizeProjectExpenseRows(file);
    case FILE_UPLOAD_TYPES.JIRA:
      return normalizeJiraRows(file);
    default:
      throw new Error('Unsupported upload type.');
    }
  };

  const submitUpload = async (cardKey) => {
    const card = cards.find((itemCard) => itemCard.key === cardKey);
    const { canAdd: cardCanAdd } = getModulePermissions(card?.module);

    if (!cardCanAdd) {
      snackbarOpen('You do not have permission to upload files.', 'warning');
      return;
    }

    const file = selectedFiles[cardKey];
    if (!file) {
      setError(cardKey, 'Please select a file to upload.');
      return;
    }

    setLoading(cardKey, true);
    setPageMessage('');

    try {
      const runBy = userProfile?.user_name
        || userProfile?.fullName
        || userProfile?.email
        || '';
      const submittedBy = userProfile?.employeeId
        || userProfile?.emp_id
        || userProfile?.userPortalId
        || '';

      const { rows, errors } = await normalizeUploadRows(cardKey, file);
      if (errors.length > 0) {
        setError(cardKey, errors.join(' | '));
        return;
      }

      const response = await UseAxios(submitProjectFileUpload({
        uploadType: cardKey,
        submittedBy,
        runBy,
        rows,
        sourceFileName: file.name,
      }));

      const apiBody = getUploadApiBody(response);
      const apiStatus = getUploadApiStatus(response);
      const message = apiBody?.message || 'File uploaded successfully';

      if (apiStatus >= 400 || apiBody?.error || apiBody?.details) {
        throw new Error(getUploadErrorMessage(response, message));
      }

      snackbarOpen(message, 'success');
      setPageMessage(message);

      if (cardKey === FILE_UPLOAD_TYPES.LEAVE_TEST) {
        setProcessedLeaveTestRows(rows);
      }
      if (cardKey === FILE_UPLOAD_TYPES.REDMINE) {
        refreshSummary();
      }
    } catch (error) {
      const message = error?.response?.data?.message
        || error?.response?.data?.error
        || error?.message
        || 'Oops! Please try again';
      setError(cardKey, message);
      snackbarOpen(message, 'warning');
    } finally {
      resetSelectedFile(cardKey);
      setLoading(cardKey, false);
    }
  };

  const handleUploadedDateSubmit = async () => {
    setManualDateError('');

    if (!canAddDate) {
      const message = 'You do not have permission to update uploaded date.';
      setManualDateError(message);
      snackbarOpen(message, 'warning');
      return;
    }

    if (!manualDate) {
      setManualDateError('Please select a date.');
      return;
    }

    try {
      const response = await UseAxios(updateLastEffortUploadedDate({
        uploadedDate: `${manualDate} ${dayjs().format('HH:mm:ss')}`,
      }));
      const payload = response?.data?.response?.data || response?.data || {};
      const message = payload?.message || 'Date Upload is Successful';
      if (response?.data?.response?.status >= 400) {
        throw new Error(payload?.message || 'Unable to update uploaded date.');
      }
      snackbarOpen(message, 'success');
      setPageMessage(message);
      refreshSummary();
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to update uploaded date.';
      setManualDateError(message);
      snackbarOpen(message, 'warning');
    }
  };

  const clearBanner = () => {
    setManualDateError('');
    setPageMessage('');
    setCardErrors({});
  };

  const downloadLeaveTestRows = async () => {
    let rows = processedLeaveTestRows;
    let errors = [];

    if (!rows) {
      const leaveTestFile = selectedFiles[FILE_UPLOAD_TYPES.LEAVE_TEST];
      if (!leaveTestFile) {
        snackbarOpen('Please select a Leave Test file first.', 'warning');
        return;
      }

      try {
        const result = await normalizeLeaveTestRows(leaveTestFile);
        rows = result.rows;
        errors = result.errors;
      } catch (error) {
        snackbarOpen(error?.message || 'Failed to process leave data.', 'warning');
        return;
      }
    }

    if (errors.length > 0) {
      snackbarOpen(errors.join(' | '), 'warning');
      return;
    }

    if (!rows || !rows.length) {
      snackbarOpen('No transformed leave rows available to download.', 'warning');
      return;
    }

    try {
      const header = ['leave_date', 'employee_id', 'total_leave', 'month_end_date'];
      const csvRows = rows.map((row) => [
        dayjs(row.leaveDate).format('DD-MMM-YYYY'),
        row.employeeId,
        row.totalLeave,
        dayjs(row.monthEndDate).format('DD-MMM-YYYY'),
      ]);
      const csvContent = [header, ...csvRows]
        .map((line) => line.join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Leave details.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      snackbarOpen('Leave data downloaded successfully.', 'success');
    } catch (error) {
      const message = error?.message || 'Failed to download transformed leave data.';
      snackbarOpen(message, 'warning');
    }
  };

  return (
    <div className="file-upload-page">
      <div className="file-upload-shell">
        <div className="file-upload-topbar">
          <div className="file-upload-title">File Upload</div>
          <div className="file-upload-summary">
            <div className="last-uploaded">
              <span className="last-uploaded-label">Last Effort Uploaded on:</span>
              <span className="last-uploaded-value">{normalizeDateLabel(lastEffortDate)}</span>
            </div>
            <div className="uploaded-date-field">
              <input
                type="date"
                value={manualDate}
                onChange={(event) => setManualDate(event.target.value)}
                disabled={!canAddDate}
              />
              <button type="button" onClick={handleUploadedDateSubmit} disabled={!canAddDate}>
                Submit
              </button>
            </div>
          </div>
        </div>

        {bannerText.length > 0 ? (
          <div className={`file-upload-banner ${bannerVariant}`}>
            <div className="file-upload-banner-content">
              {bannerText.map((message) => (
                <div key={message} className="file-upload-banner-line">
                  {message}
                </div>
              ))}
            </div>
            <button type="button" className="file-upload-banner-close" onClick={clearBanner}>
              ×
            </button>
          </div>
        ) : null}

        <div className="file-upload-grid">
          {cardRows.map((row, rowIndex) => (
            <div className="upload-row" key={rowIndex}>
              {row.map((card) => {
                const { canAdd: cardCanAdd, canView: cardCanView } = getModulePermissions(card.module);
                return (
                  <UploadTile
                    key={card.key}
                    card={card}
                    file={selectedFiles[card.key]}
                    uploading={loadingCards[card.key]}
                    canAdd={cardCanAdd}
                    canView={cardCanView}
                    onFileChange={handleFileChange(card.key)}
                    onUpload={() => submitUpload(card.key)}
                    onClear={() => clearFile(card.key)}
                    onSampleDownload={downloadSampleFile}
                    onDownload={card.key === FILE_UPLOAD_TYPES.LEAVE_TEST ? downloadLeaveTestRows : undefined}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
